import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'
import { Layout, Text } from '@loadsmart/miranda-react'

import { Icon } from 'core/ui/components/icon/Icon'
import { useWindowWidth } from 'core/ui/hooks/useWindowWidth'

import I18n from 'legacy/components/I18n/i18n'
import { Appointment } from 'legacy/components/LoadOffer/Appointment'
import { TimelineItem } from 'legacy/components/VerticalTimeline/TimelineItem'
import { getFacility } from 'legacy/services/facilities'
import { intSizes } from 'legacy/styles/screen'
import { getStopInfo, LFD, VESSEL_CUT, VESSEL_ETA } from 'legacy/utils/getStopInfo'
import { sentry } from 'legacy/utils/sentry'
import { user } from 'legacy/utils/user'

import { formatFacilityAddress } from 'facilities/presenters/facilityPresenter'
import { FacilityRating } from 'facilities/ui/FacilityRating'
import { useFacility } from 'facilities/ui/hooks/useFacility'

import { useAnalytics } from '../../hooks/useAnalytics'
import {
  AppointmentDesktop,
  AppointmentMobile,
  Details,
  FacilityInfo,
  IconContainer,
  StyledLink,
  StyledLocation,
  Warehouse,
  WarehouseLocation,
} from './LoadStop.styles'

const TWO_HOURS = 120
const ONE_HOUR = 60

function LoadStop({ stop, isLoadCandidate }) {
  const { zipcode, city, state, facility_id, requirements } = stop
  const { facility: facilityInfo, getFacilityInfo } = useFacility()
  const { report } = facilityInfo || {}

  const requiresDropTrailer = requirements.includes('drop_trailer')

  const isUserAuthenticated = useMemo(() => user.isLoggedIn(), [])

  const [facility, setFacility] = useState()
  const [facilityLoading, setFacilityLoading] = useState(!!(isUserAuthenticated && facility_id))

  const { isPickup, appointmentLabel } = getStopInfo({ stop })

  const isPortStop = useMemo(
    () => [LFD, VESSEL_CUT, VESSEL_ETA].includes(appointmentLabel),
    [appointmentLabel]
  )
  const isMobile = useWindowWidth() < intSizes.md
  const timelineMarginSidesLeft = isMobile ? 8 : 14
  const timelineMarginSidesRight = isMobile ? 8 : 9

  const formattedFacilityAddress = formatFacilityAddress({
    city,
    state,
    zipcode,
  })
  const dwellTime = facility && facility.avg_time_spent[isPickup ? 'pickup' : 'delivery']
  const formattedDwellTime =
    typeof dwellTime === 'number' ? (dwellTime > 120 ? '+2 hrs' : `${dwellTime} mins`) : '-'
  const detentionRate = facility && facility.detention_rate
  const formattedDetentionRate = typeof detentionRate === 'number' ? `${detentionRate}%` : '-'
  const { analytics } = useAnalytics()

  const showDwellTime = typeof dwellTime === 'number' && dwellTime < TWO_HOURS
  const isFastFacility = typeof dwellTime === 'number' && dwellTime < ONE_HOUR
  const fastFacilityTextColor = isFastFacility ? 'color-primary-60' : 'color-text-primary'

  const showAppointmentTimestamp = !isLoadCandidate

  const trackFacilityLink = useCallback(() => {
    analytics.track(['Load Details / Facility Card / Click'])
  }, [analytics])

  const updateFacility = useCallback(async () => {
    if (!facility_id || isPortStop) {
      setFacility()
      return setFacilityLoading(false)
    }

    setFacilityLoading(true)

    try {
      const { data: facilityData } = await getFacility(facility_id)
      setFacility(facilityData)
    } catch (error) {
      if (!error.response?.status) {
        sentry.log(error)
      }
      analytics.track(['Instant Loads', 'Load Details', 'Facility Fetch Error'], {
        description: error.response?.data?.detail,
        status: error.response?.status,
      })
    } finally {
      setFacilityLoading(false)
    }
  }, [analytics, facility_id, isPortStop])

  useEffect(() => {
    if (isUserAuthenticated) {
      updateFacility()
    }
  }, [isUserAuthenticated, updateFacility])

  useEffect(() => {
    try {
      if (facility_id) getFacilityInfo(facility_id)
    } catch (error) {
      if (!error.response?.status) {
        sentry.log(error)
      }
      analytics.track(['Instant Loads', 'Load Details', 'Facility Review Report Fetch Error'], {
        description: error.response?.data?.detail,
        status: error.response?.status,
      })
    }
  }, [analytics, facility_id, getFacilityInfo])

  return (
    <>
      <TimelineItem
        first={isPickup}
        top={12}
        margin={{ left: timelineMarginSidesLeft, right: timelineMarginSidesRight }}
        color={getToken('color-neutral-dark')}
      >
        <StyledLocation location={stop} showTag showDropTrailer={requiresDropTrailer} />
        <AppointmentMobile>
          <Appointment appointment={stop} strongDate showIcon showTime={showAppointmentTimestamp} />
        </AppointmentMobile>
      </TimelineItem>
      <Details>
        <Warehouse>
          <TimelineItem
            margin={{ left: timelineMarginSidesLeft - 1, right: timelineMarginSidesRight }}
            noBadge
          >
            {facility_id && (
              <StyledLink
                to={`/facilities/${facility_id}`}
                onClick={trackFacilityLink}
                target="_blank"
              >
                <Layout.Group align="center">
                  <Layout.Stack style={{ flex: 1 }}>
                    <WarehouseLocation>
                      <Text variant="body-md-bold">FACILITY ADDRESS</Text>
                      <Text variant="body-md" color="color-neutral-80">
                        {formattedFacilityAddress}
                      </Text>
                    </WarehouseLocation>
                    {!isPortStop && (
                      <FacilityInfo isVisible={!facilityLoading}>
                        {report && <FacilityRating report={report} inline={true} />}
                        <Layout.Group gap="spacing-1">
                          <Icon name="detention" height={14} width={14} />
                          <Text variant="body-sm" color="color-neutral-80">
                            {I18n.get('lb.loadDetails.information.detention')}
                          </Text>

                          <Text variant="body-sm-bold">{formattedDetentionRate}</Text>
                        </Layout.Group>

                        {showDwellTime && (
                          <Layout.Group gap="spacing-1" align="center">
                            <Icon name="clock" height={14} width={14} />
                            <Text variant="body-sm" color="color-neutral-80">
                              {I18n.get('lb.loadDetails.information.dwellTime')}
                            </Text>

                            <Text variant="body-sm-bold" color={fastFacilityTextColor}>
                              {formattedDwellTime}
                            </Text>
                          </Layout.Group>
                        )}
                      </FacilityInfo>
                    )}
                  </Layout.Stack>
                  <IconContainer>
                    <Icon name="arrow-right" height={12} width={12} color="neutral-darker" />
                  </IconContainer>
                </Layout.Group>
              </StyledLink>
            )}
          </TimelineItem>
        </Warehouse>
        <AppointmentDesktop>
          <Appointment
            appointment={stop}
            strongDate
            showTime={showAppointmentTimestamp}
            includeLabel
          />
        </AppointmentDesktop>
      </Details>
    </>
  )
}

LoadStop.propTypes = {
  stop: PropTypes.shape({}),
  isLoadCandidate: PropTypes.bool,
}

LoadStop.defaultProps = {
  stop: {},
  isLoadCandidate: false,
}

export { LoadStop }
